import React, { Component } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import axios from 'axios';
import { authService } from '../../services/authService';
import { API_BASE_URL } from '../../config/api';

import { 
  Table, 
  Button, 
  Space, 
  Tag, 
  Grid, 
  Row, 
  Col,
  Input,
  Select,
  Divider, 
  Spin,
  Tooltip,
} from 'antd';

import { 
  EditOutlined, 
  PrinterOutlined, 
  SearchOutlined, 
} from '@ant-design/icons';

import RequisitionSubmitModal from '../OrderEntry/RequisitionSubmitModal'


const { useBreakpoint } = Grid;

class OrderHistory extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      items: [],
      skip: 0,
      limit: 20,
      totalCount: 0,
      clientCode: props.client.ClientCode,
      id: '',
      showSubmitModal: false,

      filters: {
        requisitionID: '',
        lastName: '',
        firstName: '',
        dob: '',
        physician: '',
        orderType: '',
      },
      physicians: [],
    };
  }

  componentDidMount = () => {
    this.loadPhysicians()
    this.loadData()
  }

  loadPhysicians = async () => {
    const token = await authService.ensureValidToken();
    if (!token) {
      console.error('Not authenticated');
      return;
    }

    let params = {
      clientCode: this.state.clientCode
    }
    axios({
      method: 'GET',
      url: `${API_BASE_URL}/connect/physicians/search`,
      params: params,
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      this.setState({
        physicians: res.data.physicians,
      })
    }).catch(err =>{
      console.log(err);
    })
  }

  handleFilterChange = (field, value) => {
    this.setState((prev) => ({
      filters: {
        ...prev.filters,
        [field]: value,
      },
    }));
  };

  clearFilters = () => {
    this.setState({
      filters: {
        requisitionID: '',
        lastName: '',
        firstName: '',
        dob: '',
        physician: '',
        orderType: '',
      },
    });
  };

  loadData = async () => {
    const token = await authService.ensureValidToken();
    if (!token) {
      console.error('Not authenticated');
      return;
    }
    const params = {
      skip: this.state.skip,
      limit: this.state.limit,
      requisitionID: this.state.filters.requisitionID || undefined,
      lastName: this.state.filters.lastName || undefined,
      firstName: this.state.filters.firstName || undefined,
      dob: this.state.filters.dob || undefined,
      physicianCode: this.state.filters.physician || undefined,
      orderType: this.state.filters.orderType || undefined,
    }

    axios({
      method: 'GET',
      url: `${API_BASE_URL}/connect/requisitions/${this.state.clientCode}`,
      params:params,
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      this.setState({
        items: res.data.items,
        totalCount: res.data.totalCount,
        loading: false,
      })
    }).catch(err =>{
      console.log(err);
    })
  }

  onClose = () => {
    this.setState({
      showSubmitModal: false
    })
  }

  onOpen = (id) => {
    this.setState({
      showSubmitModal: true,
      id:id
    })
  }

  onEdit = (item) => {
    console.log(item)
    this.props.navigate(`/order-entry/${item}`)
  }

  render() {
    const { screens } = this.props;
    const isSmall = !screens?.lg;

    const shortStatus = (status) => {
      if (!status) return '';
      if (!isSmall) return status;
      if (status === 'Draft') return 'DRF';
      if (status === 'Submitted') return 'SBM';
      if (status === 'Accessioned') return 'ACC';
      return status;
    };

    const columns = [
      {
        title: 'Requisition',
        dataIndex: 'requisitionID',
        key: 'requisitionID',
        responsive: ['md'],
      },
       {
        title: 'Patient Name',
        key: 'patientName',
        render: (_, record) => {
          const name = `${record.lastName || ''}, ${record.firstName || ''}`.trim().replace(/^, /, '');
          const hc = record.houseCall;
          return (
            <div>
              <div>{name}</div>
              {hc && (
                <div style={{ color: '#888', fontSize: 12, lineHeight: 1.2, marginTop: 2 }}>
                  House Call Status:{' '}
                  <Tag
                    color={
                      hc.status === 'Completed' ? 'green'
                      : hc.status === 'Canceled' ? 'red'
                      : hc.status === 'Unable to Reach' || hc.status === 'Need to Follow Up' ? 'volcano'
                      : hc.status === 'Scheduled' ? 'blue'
                      : 'gold'
                    }
                    style={{ fontSize: 11, lineHeight: '16px', padding: '0 6px', marginRight: 0 }}
                  >
                    {hc.status}
                  </Tag>
                  <span style={{ marginLeft: 12 }}>
                    {hc.status === 'Completed'
                      ? (hc.completedDate
                          ? `Completed: ${moment(hc.completedDate, 'YYYY-MM-DD').format('MM/DD/YYYY')}`
                          : '')
                      : hc.scheduledDate
                        ? `Scheduled: ${moment(hc.scheduledDate, 'YYYY-MM-DD').format('MM/DD/YYYY')}`
                        : 'Not Scheduled'}
                  </span>
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: 'DOB',
        dataIndex: 'dob',
        key: 'dob',
        responsive: ['md'],
        render: (value) => value ? moment(value, 'YYYY-MM-DD').format('MM/DD/YYYY') : '',
      },
      {
        title: 'Physician',
        key: 'physicianName',
        responsive: ['lg'],
        render: (_, record) => {
          const d = record.physician || {};
          // "LAST, First" or "First Last" – choose your UI preference
          return `${d.lastName || ''}, ${d.firstName || ''}`.trim().replace(/^, /, '');
        },
      },
      {
        title: 'Collected',
        dataIndex: 'collectionDate',
        key: 'collectionDate',
        responsive: ['md'],
        render: (value) => value ? moment(value, 'YYYY-MM-DD HH:mm:ss').format("MM/DD/YYYY") : '',
      },
      {
        title: 'Order Type',
        dataIndex: 'orderType',
        key: 'orderType',
        responsive: ['md'],
        render: (value) => value === 'HomedrawStanding' ? 'Standing Homedraw' : value,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (_, record) => {
          const label = shortStatus(record.status);

          return (
            <Tag
              color={
                record.status === 'Accessioned'
                  ? 'green'
                  : record.status === 'Submitted'
                  ? 'orange'
                  : 'default'
              }
              style={isSmall ? { padding: '0 6px' } : undefined}
            >
              {label}
            </Tag>
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => (
          <Space>
            {record.status !== 'Accessioned' ?
              <Tooltip placement="top" title="Edit Requisition">
                <Button size="small" onClick={() => this.onEdit(record.id)}>
                  <EditOutlined />
                </Button>
              </Tooltip> : null
            }

            <Tooltip placement="top" title="Print Requisition">
              <Button size="small" type="primary" onClick={() => this.onOpen(record.id)}>
                <PrinterOutlined />
              </Button>
            </Tooltip>
            
          </Space>
        ),
      },
    ];

    return (
      <Row>
        <Col span={24}>
          <Space wrap style={{ width: '100%' }}>
            <Input
              placeholder="Requisition ID"
              allowClear
              value={this.state.filters.requisitionID}
              onChange={(e) => this.handleFilterChange('requisitionID', e.target.value)}
              style={{ width: 150 }}
            />

            <Input
              placeholder="Last Name"
              allowClear
              value={this.state.filters.lastName}
              onChange={(e) => this.handleFilterChange('lastName', e.target.value)}
              style={{ width: 180 }}
            />

            <Input
              placeholder="First Name"
              allowClear
              value={this.state.filters.firstName}
              onChange={(e) => this.handleFilterChange('firstName', e.target.value)}
              style={{ width: 180 }}
            />

            <Input
              placeholder="DOB"
              allowClear
              value={this.state.filters.do}
              onChange={(e) => this.handleFilterChange('dob', e.target.value)}
              style={{ width: 150 }}
            />

            <Select
              placeholder="Physician"
              allowClear
              value={this.state.filters.physician || undefined}
              onChange={(value) => this.handleFilterChange('physician', value)}
              style={{ width: 180 }}
            >
              {this.state.physicians.map(item =>
                <Select.Option key={item.id} value={item.PhysicianCode}>{item.LastName}, {item.FirstName}</Select.Option>
              )}
            </Select>

            <Select
              placeholder="Order Type"
              allowClear
              value={this.state.filters.orderType || undefined}
              onChange={(value) => this.handleFilterChange('orderType', value)}
              style={{ width: 180 }}
              options={[
                {value: 'Routine', label: 'Routine'},
                {value: 'Stat', label: 'Stat'},
                {value: 'Homedraw', label: 'Homedraw'},
                {value: 'HomedrawStanding', label: 'Standing Homedraw'},
              ]}
            />

            <Button type="primary"onClick={() => {
              this.setState({ skip: 0 }, () => this.loadData());
            }}><SearchOutlined /> Search</Button>
            <Button onClick={this.clearFilters}>Clear</Button>
          </Space>
        </Col>
        <Divider/>
        <Col span={24}>

          {this.state.loading ?
            <div style={{textAlign:"center"}}>
              <Spin size="large"/>
            </div>          
            :
            <Table
              dataSource={this.state.items}
              columns={columns}
              pagination={{
                current: Math.floor(this.state.skip / this.state.limit) + 1,
                pageSize: this.state.limit,
                total: this.state.totalCount,
                showSizeChanger: true,
                pageSizeOptions: ['10', '25', '50', '100'],
                onChange: (page) => {
                  this.setState(
                    { skip: (page - 1) * this.state.limit },
                    () => this.loadData()
                  );
                },
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              }}
              size="small"
              rowKey="id"
            />
          }
        </Col>
        {this.state.showSubmitModal && 
          <RequisitionSubmitModal 
            id={this.state.id}  
            printers={this.props.printers}
            onClose={this.onClose}
            showSubmitModal={this.state.showSubmitModal}
          /> 
        }  
      </Row>      
    );
  }
}
export default function OrderHistoryWithBreakpoints(props) {
  const screens = useBreakpoint();
  const navigate = useNavigate();
  return <OrderHistory {...props} screens={screens} navigate={navigate}/>;
}
